import React from "react";
import PropTypes from "prop-types";
import { themr } from "react-css-themr";
import classnames from "classnames";
import { graphql } from "react-apollo";
import ruLocale from "date-fns/locale/ru";
import { Labeled, Badge, Link, Loading } from "@lifepay/rc-ui-toolkit";

import { transaction } from "model/gql/queries";
import CardNumber from "components/utils/CardNumber";
import HumanReadableDate from "components/utils/HumanReadableDate";
import HintedCurrencyCode from "components/utils/HintedCurrencyCode";
import HintedMCC from "components/utils/HintedMCC";
import TransactionOriginType from "components/processing/transaction/TransactionOriginType";
import SMSStatusLabel from "components/processing/transaction/SMSStatusLabel";
import SMSError from "components/processing/transaction/SMSError";
import UCAFType from "components/processing/transaction/UCAFType";

import styles from "./Transaction.m.scss";

function ExtendedTransaction(props) {
  const {
    theme,
    item: selectedItem,
    goToCompanies,
    goToMerchants,
    goToTerminals,
    data,
    selectTransaction,
  } = props;

  const loading = data && data.loading;

  const item =
    data && data[transaction.dataKey]
      ? data[transaction.dataKey]
      : selectedItem;

  return loading ? (
    <Loading />
  ) : (
    <div className={classnames(theme.extended)}>
      <div className={classnames(theme["grid-container"])}>
        <div className={classnames(theme.id)}>
          {`Транзакция id: ${item.transactionId}`}
        </div>
        <div className={classnames(theme.type)}>
          <Badge type={item.isReversal ? "blue" : "green"}>{item.type}</Badge>
        </div>
        <div className={classnames(theme.status)}>
          <Badge type={item.status === "0" ? "green" : "red"}>
            {item.status === "0"
              ? item.statusName
              : `Ошибка ${item.status}: ${item.statusName}`}
          </Badge>
        </div>
        <div className={classnames(theme.return)}>
          {item.isReversal ? (
            <Badge type={"gray"}>
              {`отмена для `}
              <Link
                withRouter={false}
                type={"alt"}
                onClick={() =>
                  selectTransaction({
                    transactionId: item.transactionId,
                    isReversal: false,
                  })
                }
              >
                {"операции"}
              </Link>
            </Badge>
          ) : null}
          {item.hasReversal ? (
            <Badge type={"blue"}>
              {`для операции есть `}
              <Link
                withRouter={false}
                type={"alt"}
                onClick={() =>
                  selectTransaction({
                    transactionId: item.transactionId,
                    isReversal: true,
                  })
                }
              >
                {"отмена"}
              </Link>
            </Badge>
          ) : null}
        </div>
        <div className={classnames(theme.date)}>
          <HumanReadableDate
            date={item.date}
            locale={ruLocale}
            NoDate={<div>{"-"}</div>}
          />
        </div>
        <div className={classnames(theme.authCode)}>
          <Labeled label={"Код авторизации"}>{item.authCode || "-"}</Labeled>
        </div>
        <div className={classnames(theme.rrn)}>
          <Labeled label={"RRN"}>{item.rrn || "-"}</Labeled>
        </div>

        <div className={classnames(theme.card)}>
          <div className={classnames(theme["payment-system"])}>
            <Labeled label={"Платежная система"} theme={{ value: theme.value }}>
              {item.paymentSystem || "-"}
            </Labeled>
          </div>
          <div className={classnames(theme.pan)}>
            <Labeled label={"PAN"}>
              <CardNumber pan={item.pan} />
            </Labeled>
          </div>
          <div className={classnames(theme["origin-type"])}>
            <Labeled label={"Источник транзакции"}>
              <TransactionOriginType type={item.originType} />
            </Labeled>
          </div>
          <div className={classnames(theme["pos-entry-mode"])}>
            <Labeled label={"Способ ввода карты"}>
              {item.posEntryMode || "-"}
            </Labeled>
          </div>
          <div className={classnames(theme.eci)}>
            <Labeled label={"ECI"}>{item.ecommerceIndicator || "-"}</Labeled>
          </div>
          <div className={classnames(theme.ucaf)}>
            <Labeled label={"3DS"}>
              {item.ucafIndicator ? (
                <UCAFType type={item.ucafIndicator} />
              ) : (
                "-"
              )}
            </Labeled>
          </div>
        </div>
        <div className={classnames(theme["response-code"])}>
          {item.status !== "0" && (
            <Badge type={"red"}>
              {item.responseCode
                ? `Отказ эмитента. Код ответа: ${item.responseCode}`
                : "Отказ ПЦ РФИ"}
            </Badge>
          )}
        </div>
        <div className={classnames(theme.sms)}>
          <div className={classnames(theme["sms-header"])}>
            <div>{`SMS`}</div>
            <div>{item.smsPhone && `на номер: ${item.smsPhone}`}</div>
            <div>{item.smsDate && `отправлено`}</div>
            {item.smsDate && (
              <HumanReadableDate date={item.smsDate} locale={ruLocale} />
            )}
          </div>
          <SMSStatusLabel status={item.smsStatus} />
          <SMSError status={item.smsStatus} />
          {item.smsText && (
            <Labeled label={"Текст SMS"}>{item.smsText}</Labeled>
          )}
        </div>
        <div className={classnames(theme.amounts)}>
          <div className={classnames(theme.label)}>{"Сумма операции"}</div>
          <div className={classnames(theme.amount)}>{item.requestAmount}</div>
          <div className={classnames(theme.currency)}>
            {item.transactionCurrencyCode ? (
              <HintedCurrencyCode code={item.transactionCurrencyCode} />
            ) : (
              "-"
            )}
          </div>
          <div className={classnames(theme.label)}>{"В валюте карты"}</div>
          <div className={classnames(theme.amount)}>{item.billingAmount}</div>
          <div className={classnames(theme.currency)}>
            {item.cardCurrencyCode ? (
              <HintedCurrencyCode code={item.cardCurrencyCode} />
            ) : (
              "-"
            )}
          </div>
          <div className={classnames(theme.label)}>{"Комиссия"}</div>
          <div className={classnames(theme.amount)}>{item.fee}</div>
          <div className={classnames(theme.currency)}>
            {item.cardCurrencyCode ? (
              <HintedCurrencyCode code={item.cardCurrencyCode} />
            ) : (
              "-"
            )}
          </div>
        </div>
        <div className={classnames(theme.category)}>
          <Labeled label={"Acquiring Institution ID"}>
            {item.acquiringId}
          </Labeled>
          <Labeled label={"Acquiring Institution Name"}>
            {item.acquiringName}
          </Labeled>
          {/* <Labeled label={"Id компании"}>
            {item.companyId ? (
              <Link
                withRouter={false}
                type={"alt"}
                onClick={() =>
                  goToCompanies({ search: `company-id=${item.companyId}` })
                }
              >
                {item.companyId}
              </Link>
            ) : (
              "-"
            )}
          </Labeled> */}
          <Labeled label={"Компания"}>
            {item.companyName ? (
              <Link
                withRouter={false}
                type={"alt"}
                onClick={() =>
                  goToCompanies({
                    search: `company-name=${item.companyName}`,
                    state: {
                      routerStateQuery: {
                        companyName: item.companyName,
                      },
                    },
                  })
                }
              >
                {item.companyName}
              </Link>
            ) : (
              "-"
            )}
          </Labeled>
          <Labeled label={"Точка обслуживания"}>
            {item.merchantName ? (
              <Link
                withRouter={false}
                type={"alt"}
                onClick={() =>
                  goToMerchants({
                    search: `merchant-name=${item.merchantName}`,
                    state: {
                      routerStateQuery: {
                        merchantName: item.merchantName,
                      },
                    },
                  })
                }
              >
                {item.merchantName}
              </Link>
            ) : (
              "-"
            )}
          </Labeled>
          <Labeled label={"Id терминала"}>
            {item.terminalId ? (
              <Link
                withRouter={false}
                type={"alt"}
                onClick={() =>
                  goToTerminals({
                    search: `terminal-ids=${item.terminalId}`,
                    state: {
                      routerStateQuery: {
                        terminalIds: item.terminalId,
                      },
                    },
                  })
                }
              >
                {item.terminalId}
              </Link>
            ) : (
              "-"
            )}
          </Labeled>
          <Labeled label={"Страна точки обслуживания"}>
            {item.merchantCountry}
          </Labeled>
          <Labeled label={"Тип терминала"}>{item.device}</Labeled>
          <div className={classnames(theme.mcc)}>
            <Labeled label={"MCC терминала"}>
              {item.mcc ? <HintedMCC code={item.mcc} /> : "-"}
            </Labeled>
          </div>
        </div>
      </div>
    </div>
  );
}

ExtendedTransaction.displayName = "ExtendedTransaction";

ExtendedTransaction.propTypes = {
  theme: PropTypes.object,
  item: PropTypes.object,
  data: PropTypes.object,
  goToCompanies: PropTypes.func,
  goToTerminals: PropTypes.func,
  goToMerchants: PropTypes.func,
  goToTransactions: PropTypes.func,
  selectTransaction: PropTypes.func,
};

ExtendedTransaction.defaultProps = {};

export default themr("@lifepay/ExtendedTransaction", styles)(
  graphql(transaction.query, transaction.config)(ExtendedTransaction)
);



// WEBPACK FOOTER //
// ./src/components/list-items/compact/Transaction/Extended.js